import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import api from "../../api/client";
import LangTabs, { LANG_CODES } from "./LangTabs";

const ICONS = ["Stethoscope", "MessageSquare", "Heart", "Footprints", "Brain", "Users", "Eye", "Zap", "Activity", "Sparkles"];

const empty = () => ({
  name: "", desc: "", icon: "Stethoscope", staff_id: null, order: 0,
  i18n: { fr: { name: "", desc: "" }, nl: { name: "", desc: "" }, en: { name: "", desc: "" }, it: { name: "", desc: "" } },
});

export default function ServicesAdmin() {
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [lang, setLang] = useState("fr");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/services").then((r) => setServices(r.data));
  useEffect(() => {
    load();
    api.get("/staff").then((r) => setStaffList(r.data));
  }, []);

  const startEdit = (s) => {
    const i18n = s.i18n || {};
    LANG_CODES.forEach((l) => { i18n[l] = i18n[l] || { name: "", desc: "" }; });
    setEditing({ ...s, i18n });
    setLang("fr"); setErr("");
  };
  const startCreate = () => { setEditing(empty()); setLang("fr"); setErr(""); };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const payload = {
        ...editing,
        name: editing.i18n.fr.name || editing.name,
        desc: editing.i18n.fr.desc || editing.desc,
        staff_id: editing.staff_id || null,
      };
      if (editing.id) {
        await api.put(`/services/${editing.id}`, payload);
      } else {
        await api.post("/services", payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce service ?")) return;
    await api.delete(`/services/${id}`);
    load();
  };

  const setI18n = (field, val) =>
    setEditing((e) => ({ ...e, i18n: { ...e.i18n, [lang]: { ...e.i18n[lang], [field]: val } } }));

  return (
    <div data-testid="services-admin">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-slate-900">Services</h1>
          <p className="text-slate-500 text-sm mt-1">{services.length} service(s)</p>
        </div>
        <button onClick={startCreate} className="gradient-bg text-white text-sm font-semibold px-5 py-2.5 rounded-full flex items-center gap-2" data-testid="service-create-btn">
          <Plus className="w-4 h-4" /> Nouveau service
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {services.map((s) => {
          const linkedStaff = staffList.find((x) => x.id === s.staff_id);
          return (
            <div key={s.id} className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center gap-4">
              <div className="w-12 h-12 gradient-bg rounded-2xl flex items-center justify-center text-white text-xs font-semibold">{s.icon?.slice(0, 2) || "Sv"}</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{s.name}</p>
                <p className="text-xs text-slate-500 truncate">→ {linkedStaff ? linkedStaff.name : "Pas lié à un professionnel"}</p>
              </div>
              <button onClick={() => startEdit(s)} className="p-2 rounded-full hover:bg-slate-50 text-slate-600" data-testid={`edit-service-${s.id}`}>
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => remove(s.id)} className="p-2 rounded-full hover:bg-rose-50 text-rose-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="service-editor-modal">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit text-2xl font-bold text-slate-900">{editing.id ? "Modifier le service" : "Nouveau service"}</h2>
              <button onClick={() => setEditing(null)} className="p-2 rounded-full hover:bg-slate-50"><X className="w-5 h-5" /></button>
            </div>

            {err && <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-2xl p-3">{err}</div>}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Icône</label>
                <select value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm">
                  {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Ordre</label>
                <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value, 10) || 0 })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Professionnel lié</label>
                <select value={editing.staff_id || ""} onChange={(e) => setEditing({ ...editing, staff_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm">
                  <option value="">— Aucun —</option>
                  {staffList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>

            <LangTabs active={lang} onChange={setLang} />

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nom du service ({lang.toUpperCase()})</label>
                <input type="text" value={editing.i18n[lang].name} onChange={(e) => setI18n("name", e.target.value)}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" data-testid={`service-name-${lang}`} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Description courte ({lang.toUpperCase()})</label>
                <textarea value={editing.i18n[lang].desc} onChange={(e) => setI18n("desc", e.target.value)} rows={3}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-full">Annuler</button>
              <button onClick={save} disabled={saving} className="gradient-bg text-white text-sm font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-60" data-testid="service-save-btn">
                <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
